<template>
  <div class="table-select-all-header">
    <BittsCheckbox
      v-if="canSelectRows"
      :checked="isAllSelected"
      :indeterminate="isSomeSelected"
      @input="handleToggleAll"
      class="mr-8"
    />
    <span>{{ params.displayName }}</span>
  </div>
</template>

<script setup lang="ts">
import { BittsCheckbox } from '@crossbeam/bitts';

import { IHeaderParams } from '@ag-grid-community/core';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const { params } = defineProps<{
  params: IHeaderParams;
}>();

const canSelectRows = computed(
  () => params.context?.canSelectRows?.() ?? false,
);

const selectedCount = ref(0);
const selectableCount = ref(0);

function updateSelectionState() {
  selectedCount.value = params.api.getSelectedNodes().length;
  // Counted rather than read from getDisplayedRowCount(): selectAll() skips
  // rows isRowSelectable rejects and ignores filters, so a displayed-row count
  // can exceed what selectAll() actually selects and the header would never
  // reach "all selected".
  let selectable = 0;
  params.api.forEachNode((node) => {
    if (node.selectable) selectable++;
  });
  selectableCount.value = selectable;
}

const isAllSelected = computed(
  () =>
    selectableCount.value > 0 && selectedCount.value === selectableCount.value,
);
const isSomeSelected = computed(
  () => selectedCount.value > 0 && selectedCount.value < selectableCount.value,
);

function handleToggleAll(checked: boolean) {
  if (checked) params.api.selectAll();
  else params.api.deselectAll();
}

onMounted(() => {
  params.api.addEventListener('selectionChanged', updateSelectionState);
  updateSelectionState();
});

onBeforeUnmount(() => {
  if (params.api.isDestroyed()) return;
  params.api.removeEventListener('selectionChanged', updateSelectionState);
});
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-select-all-header {
  @apply flex items-center gap-10;
}
</style>
